import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { eventsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Loader2, Minus, Plus, RefreshCw, ArrowRightLeft, 
  MessageSquare, Send, User, Clock, DollarSign, Package, Truck
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const eventTypeConfig = {
  use: { icon: Minus, color: 'text-destructive', bgColor: 'bg-destructive/10', label: 'Used' },
  restock: { icon: Plus, color: 'text-success', bgColor: 'bg-success/10', label: 'Restocked' },
  adjust: { icon: RefreshCw, color: 'text-warning', bgColor: 'bg-warning/10', label: 'Adjusted' },
  transfer_out: { icon: ArrowRightLeft, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Transferred Out' },
  transfer_in: { icon: ArrowRightLeft, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Received' },
  import_new: { icon: Plus, color: 'text-success', bgColor: 'bg-success/10', label: 'Imported (New)' },
  import_merge: { icon: RefreshCw, color: 'text-primary', bgColor: 'bg-primary/10', label: 'Imported (Merged)' },
  import_replace: { icon: RefreshCw, color: 'text-warning', bgColor: 'bg-warning/10', label: 'Imported (Replaced)' },
};

export const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const [eventRes, commentsRes] = await Promise.all([
          eventsAPI.get(id),
          eventsAPI.getComments(id),
        ]);
        setEvent(eventRes.data);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Failed to load event:', error);
        toast.error('Failed to load event details');
        navigate('/activity');
      } finally {
        setLoading(false);
      }
    };
    fetchEventData();
  }, [id, navigate]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const res = await eventsAPI.addComment(id, newComment.trim());
      setComments([...comments, res.data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Event not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/activity')}>
            Back to Activity
          </Button>
        </div>
      </Layout>
    );
  }

  const config = eventTypeConfig[event.event_type];
  const Icon = config?.icon || RefreshCw;
  const isNegative = event.qty_delta < 0;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/activity')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-heading text-foreground">Event Details</h1>
            <p className="text-sm text-muted-foreground">
              {format(new Date(event.created_at), 'MMMM d, yyyy \'at\' h:mm a')}
            </p>
          </div>
        </div>

        {/* Event Summary Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 ${config?.bgColor || 'bg-muted'} rounded-xl flex items-center justify-center shrink-0`}>
                <Icon className={`w-7 h-7 ${config?.color || 'text-foreground'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xl font-bold text-foreground">
                      {config?.label || event.event_type}
                    </p>
                    <p className="text-lg font-semibold text-foreground mt-1">
                      {event.item_name}
                    </p>
                  </div>
                  <div className={`text-3xl font-mono font-bold ${isNegative ? 'text-destructive' : 'text-success'}`}>
                    {isNegative ? '' : '+'}{event.qty_delta}
                  </div>
                </div>
                {event.note && (
                  <p className="mt-3 text-muted-foreground italic bg-muted/50 p-3 rounded-lg">
                    "{event.note}"
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vehicle Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Truck className="w-4 h-4" /> Vehicle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link 
                to={`/vans/${event.van_id}`}
                className="text-lg font-semibold text-primary hover:underline"
              >
                {event.van_name}
              </Link>
              <div className="text-sm text-muted-foreground mt-1">
                {event.van_number && <span>{event.van_number}</span>}
                {event.van_license_plate && <span> · {event.van_license_plate}</span>}
              </div>
            </CardContent>
          </Card>

          {/* Item Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Package className="w-4 h-4" /> Item
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold text-foreground">{event.item_name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                SKU: {event.item_sku || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {/* User Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" /> Performed By
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold text-foreground">{event.user_name}</p>
              <p className="text-sm text-muted-foreground">{event.user_email}</p>
            </CardContent>
          </Card>

          {/* Financial Impact */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Financial Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Cost Impact</span>
                  <span className="font-semibold">${parseFloat(event.cost_impact || 0).toFixed(2)}</span>
                </div>
                {event.item_billable_price && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Billable Value</span>
                    <span className="font-semibold text-success">${parseFloat(event.billable_impact || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-sm text-muted-foreground">Unit Cost</span>
                  <span className="text-sm">${parseFloat(event.item_unit_cost || 0).toFixed(2)}</span>
                </div>
                {event.item_billable_price && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Billable Price</span>
                    <span className="text-sm">${parseFloat(event.item_billable_price).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Linked Transfer Event */}
        {event.linked_event_id && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ArrowRightLeft className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-semibold text-foreground">This is part of a transfer</p>
                    <p className="text-sm text-muted-foreground">
                      {event.event_type === 'transfer_out' ? 'View the receiving event' : 'View the sending event'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.linked_event_id}`)}>
                  View Linked Event
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comments Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comments ({comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Comment List */}
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No comments yet. Be the first to add context!
              </p>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{comment.user_name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-foreground">{comment.comment}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            <form onSubmit={handleAddComment} className="pt-4 border-t">
              <div className="space-y-3">
                <Textarea
                  placeholder="Add a comment about this event..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={submitting || !newComment.trim()}>
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Add Comment
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
